import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ChatPanel = ({ isCollapsed, onToggleCollapse }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: "Dr. Sarah Johnson",
      content: "Hello! I\'m ready to begin our consultation. How are you feeling today?",
      timestamp: new Date(Date.now() - 300000),
      type: "text",
      isProvider: true
    },
    {
      id: 2,
      sender: "Emily Chen",
      content: "Hi Dr. Johnson. I\'ve been experiencing some chest discomfort and shortness of breath.",
      timestamp: new Date(Date.now() - 240000),
      type: "text",
      isProvider: false
    },
    {
      id: 3,
      sender: "Emily Chen",
      content: "I\'ve attached my recent ECG results from last week.",
      timestamp: new Date(Date.now() - 180000),
      type: "file",
      fileName: "ECG_Results_2024.pdf",
      fileSize: "2.3 MB",
      isProvider: false
    },
    {
      id: 4,
      sender: "Dr. Sarah Johnson",
      content: "Thank you for sharing that. I can see the ECG results. Let me review them while we continue our discussion.",
      timestamp: new Date(Date.now() - 120000),
      type: "text",
      isProvider: true
    }
  ]);

  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (!newMessage?.trim()) return;

    const message = {
      id: messages?.length + 1,
      sender: "Dr. Sarah Johnson",
      content: newMessage,
      timestamp: new Date(),
      type: "text",
      isProvider: true
    };

    setMessages(prev => [...prev, message]);
    setNewMessage("");
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const message = {
      id: messages?.length + 1,
      sender: "Dr. Sarah Johnson",
      content: `Shared file: ${file?.name}`,
      timestamp: new Date(),
      type: "file",
      fileName: file?.name,
      fileSize: `${(file?.size / 1024 / 1024)?.toFixed(1)} MB`,
      isProvider: true
    };

    setMessages(prev => [...prev, message]);
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  if (isCollapsed) {
    return (
      <div className="w-12 bg-card border-l border-border flex flex-col items-center py-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="mb-4"
        >
          <Icon name="MessageSquare" size={20} />
        </Button>
        <div className="w-3 h-3 bg-primary rounded-full animate-pulse" />
      </div>
    );
  }

  return (
    <div className="w-80 bg-card border-l border-border flex flex-col h-full">
      {/* Chat Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Icon name="MessageSquare" size={20} className="text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">Consultation Chat</h3>
            <p className="text-xs text-muted-foreground">Real-time messaging</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
        >
          <Icon name="ChevronRight" size={16} />
        </Button>
      </div>
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map((message) => (
          <div
            key={message?.id}
            className={`flex ${message?.isProvider ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[80%] ${message?.isProvider ? 'order-2' : 'order-1'}`}>
              <div
                className={`rounded-lg p-3 ${
                  message?.isProvider
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {message?.type === 'file' ? (
                  <div className="flex items-center space-x-2">
                    <Icon name="FileText" size={16} />
                    <div>
                      <p className="text-sm font-medium">{message?.fileName}</p>
                      <p className="text-xs opacity-75">{message?.fileSize}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="w-6 h-6">
                      <Icon name="Download" size={12} />
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm">{message?.content}</p>
                )}
              </div>
              <div className="flex items-center space-x-2 mt-1">
                <p className="text-xs text-muted-foreground">{message?.sender}</p>
                <span className="text-xs text-muted-foreground">•</span>
                <p className="text-xs text-muted-foreground">{formatTime(message?.timestamp)}</p>
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg p-3 max-w-[80%]">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Message Input */}
      <div className="p-4 border-t border-border">
        <div className="flex items-end space-x-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e?.target?.value)}
              onKeyPress={handleKeyPress}
              className="resize-none"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef?.current?.click()}
          >
            <Icon name="Paperclip" size={16} />
          </Button>
          <Button
            variant="default"
            size="icon"
            onClick={handleSendMessage}
            disabled={!newMessage?.trim()}
          >
            <Icon name="Send" size={16} />
          </Button>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileUpload}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        />
        
        <p className="text-xs text-muted-foreground mt-2">
          Press Enter to send • Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default ChatPanel;